import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { Splash } from '@ofself/shell';
import { NucleusSplashMark } from '../../components/nucleus/NucleusMark';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { buildAppCallbackUrl } from '../../components/grant/appCallback';
import { loginUrlFor } from '../../utils/loginRedirect';

/**
 * Back from Stripe. Verify the payment, then finish the grant they already decided on.
 *
 * IT VERIFIES RATHER THAN ASSUMES. Stripe redirects the buyer back the instant they pay;
 * the webhook is asynchronous and may not have arrived. `confirmCheckout` pulls the
 * session from Stripe and settles the purchase itself, so a person who has paid is never
 * told they have not.
 *
 * THE SERVER RESPONSE IS THE SOURCE OF TRUTH, not the browser. `/billing/checkout/confirm`
 * returns the realm and consent they chose along with the app's client_id, so this page
 * can complete the grant with nothing kept locally. The sessionStorage stash is an
 * OPTIMISATION — it preserves the exact approve payload, including encryption material
 * that never leaves the browser — and its absence costs nothing.
 *
 * That ordering matters because the earlier version depended on the stash and dead-ended
 * without it: it navigated to `/grant?app_id=<uuid>`, and the consent page reads
 * `client_id`, so a person who had ALREADY BEEN CHARGED landed on a validation error with
 * no way forward. Cross-tab returns, restarted browsers and cleared storage all took that
 * path.
 *
 * WHAT IT LOOKS LIKE. While it works, the nucleus mark held on the splash field — the
 * same drawing the app opens with — until it hands off. It used to print each step
 * ("Payment received. Authorizing…") as bare text on a blank page, which read as a
 * debug screen at the one moment somebody has just paid. Words appear only when there
 * is something the person needs to know: still processing, or could not finish.
 */
export default function PaidReturnRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  // null while working (the splash holds); a sentence once there is something to say.
  const [problem, setProblem] = useState(null);
  // Strict mode double-invokes effects; a second confirm is harmless but a second
  // approve is not.
  const ran = useRef(false);

  useEffect(() => {
    // WAIT FOR THE IDENTITY, because the hand-back carries it. Stripe's redirect is a
    // full page load, so `user` starts null unless a cached snapshot exists, and
    // buildAppCallbackUrl spells a missing user as `user_id=` — no user at all, to
    // every app. Checked before `ran`, so the run that counts is the one with a user
    // (GrantRoute guards its hand-back the same way).
    if (!user?.id || ran.current) return;
    ran.current = true;

    const sessionId = params.get('session_id');

    /** Hand back to the app that sent them, exactly as the free path does. */
    const finish = async (redirectUri, clientId) => {
      if (redirectUri) {
        // NOT a navigate to our own app list. Apps gate on `code=success` in the
        // callback and bounce to ?error=auth_failed without it — so returning them to
        // Paradigm would leave a paid-for grant the app never learns about.
        window.location.href = await buildAppCallbackUrl(redirectUri, clientId, user);
        return;
      }
      navigate('/authorized-apps?granted=1', { replace: true });
    };

    (async () => {
      if (!sessionId) {
        navigate('/apps', { replace: true });
        return;
      }

      let confirmed;
      try {
        confirmed = await api.confirmCheckout(sessionId);
      } catch (e) {
        // PAYMENT_INCOMPLETE means Stripe has not settled it yet — a card still
        // processing, not a failure.
        setProblem(
          e?.code === 'PAYMENT_INCOMPLETE'
            ? 'Your payment is still processing. It will appear in your apps shortly.'
            : (e?.message || 'We could not confirm that payment.'));
        setTimeout(() => navigate('/billing', { replace: true }), 2500);
        return;
      }

      if (!confirmed?.ready) {
        setProblem('Your payment is still processing. It will appear shortly.');
        setTimeout(() => navigate('/billing', { replace: true }), 2500);
        return;
      }

      const stored = sessionStorage.getItem('paradigm.pendingGrant');
      let plan = null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          // Only replay a stash that belongs to THIS purchase. Cheap insurance on a
          // path that creates a grant.
          if (parsed?.clientId && confirmed.client_id
              && parsed.clientId !== confirmed.client_id) {
            plan = null;
          } else if (typeof api[parsed.call] === 'function') {
            plan = parsed;
          }
        } catch { plan = null; }
      }

      try {
        if (plan) {
          await api[plan.call](...plan.args);
          sessionStorage.removeItem('paradigm.pendingGrant');
          await finish(plan.redirectUri, plan.clientId || confirmed.client_id);
          return;
        }

        // NO STASH — rebuild from what the server kept. Everything needed is in the
        // confirm response, which is why this is a recovery rather than a dead end.
        const ctx = confirmed.context || {};
        // An EMPTY consent is not a safe default here. Approving with nothing would
        // create a grant the person never chose, which is worse than asking them to
        // finish on the consent screen.
        if (!confirmed.client_id || !ctx.privacy_realm_id || !ctx.consent?.consent_yaml) {
          throw new Error('missing the details needed to finish this authorization');
        }
        const body = {
          client_id: confirmed.client_id,
          privacy_realm_id: ctx.privacy_realm_id,
          privacy_realm_ids: [ctx.privacy_realm_id],
          ...(ctx.group_id ? { group_id: ctx.group_id } : {}),
          // The consent they gave before leaving, verbatim.
          consent_yaml: ctx.consent.consent_yaml,
        };
        await api.approveAuthorizationV2(body);
        sessionStorage.removeItem('paradigm.pendingGrant');
        await finish(null, confirmed.client_id);
      } catch (e) {
        // THE WORST BRANCH: they have been charged and hold nothing. Say that the money
        // is safe, say where to go, and send them to Billing — where the purchase is
        // listed and refundable — rather than to a page that will not charge them again
        // but also will not explain itself.
        sessionStorage.removeItem('paradigm.pendingGrant');
        setProblem(
          `Payment received${confirmed.app_name ? ` for ${confirmed.app_name}` : ''}, but `
          + 'we could not finish authorizing. Your purchase is safe and is listed under '
          + 'Billing — open the app from the store to complete it, or refund it there. '
          + 'You will not be charged again.');
        setTimeout(() => navigate('/billing', { replace: true }), 5000);
      }
    })();
  }, [params, navigate, user]);

  // Signed out: sign in and come back here, session_id and all, as /grant does.
  if (!authLoading && !user) return <Navigate to={loginUrlFor(location)} replace />;

  // ONE status element for the page's whole life, only its text changing: many screen
  // readers announce changes inside a live region that already exists, and miss one
  // that arrives as a new element. Hidden while the splash holds; shown with a problem.
  return (
    <div data-testid="paid-return" className={problem ? 'gp' : undefined}
         style={problem ? PROBLEM_STYLE : undefined}>
      {/* `ready={false}` holds the drawn mark until this page navigates away. */}
      {!problem && (
        <Splash app="nucleus" mark={<NucleusSplashMark />} grid={false} ready={false} />
      )}
      <p role="status" style={problem ? undefined : SR_ONLY}>
        {problem || 'Confirming your payment…'}
      </p>
    </div>
  );
}

const PROBLEM_STYLE = {
  maxWidth: '32rem', margin: '20vh auto 0', padding: '0 1.5rem',
  textAlign: 'center', lineHeight: 1.5,
};

const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};
